// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import constants from "@store/constants";
import submitForm from "@store/Framework/Store/Greif/Components/ContactUsTopicsForm/Handlers/SubmitForm";
import resetFieldValues from "@store/Framework/Store/Greif/Components/ContactUsTopicsForm/Handlers/ResetFieldValues";
import FormStepper, { FormStepperPresentationProps } from "@store/Library/Components/FormStepper/FormStepper";
import NavigationBlocker from "@store/Library/Components/NavigationBlocker";
import ContactUsTopicsFormContactInformationStep from "@store/Library/Components/ContactUsTopics/ContactUsTopicsFormContactInformationStep";
import ContactUsTopicsFormDetailsStep from "@store/Library/Components/ContactUsTopics/ContactUsTopicsFormDetailsStep";
import ConfirmationMessage from "@store/Library/Components/ConfirmationMessage";
import React, { FC, useContext, useEffect, useState } from "react";

const formTopContentZoneName = "FormTopContent";

const enum fields {
    topic = "topic",
    isLocationRequired = "isLocationRequired",
}

interface ContactUsTopicsFormFields {
    topic: string;
    isLocationRequired: boolean;
}

export interface ContactUsTopicsFormProps extends WidgetProps {
    fields: ContactUsTopicsFormFields;
}

export interface ContactUsTopicsFormStyles {
    formStepper?: FormStepperPresentationProps;
}

export const contactUsTopicsFormStyles: ContactUsTopicsFormStyles = {};

const styles = contactUsTopicsFormStyles;

const ContactUsTopicsForm: FC<ContactUsTopicsFormProps> = ({ fields: widgetFields, id }) => {
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const currentBillTo = useAppSelector(getCurrentBillToState)?.value;
    const formFieldValues = useAppSelector(state => state.greif.components.contactUsTopicsForm.formFieldValues);
    const { isDirty } = useAppSelector(state => state.greif.components.contactUsTopicsForm);
    const formTopContentIsActive = useZoneIsActive(id, formTopContentZoneName);

    useEffect(() => {
        return () => {
            dispatch(resetFieldValues());
        };
    }, []);

    return (
        <LoadingOverlay loading={isLoading}>
            {isSubmitted ? (
                <ConfirmationMessage
                    headline={siteMessage("ContactUsTopicsForm_ConfirmationHeadline")}
                    body={siteMessage("ContactUsTopicsForm_ConfirmationMessage")}
                    buttonText={siteMessage("ContactUsTopicsForm_ConfirmationButtonText")}
                />
            ) : (
                <>
                    {formTopContentIsActive && <Zone contentId={id} zoneName={formTopContentZoneName} />}
                    <FormStepper
                        {...styles.formStepper}
                        onSubmit={async event => {
                            event.preventDefault();

                            if (!currentBillTo) {
                                return;
                            }

                            setIsLoading(true);
                            dispatch(
                                submitForm({
                                    attachments: formFieldValues.attachments.value,
                                    emailAddress: formFieldValues.emailAddress.value,
                                    firstName: formFieldValues.firstName.value,
                                    lastName: formFieldValues.lastName.value,
                                    location: formFieldValues.location.value,
                                    message: formFieldValues.message.value,
                                    shipToNumber: formFieldValues.shipToNumber.value,
                                    soldToNumber: currentBillTo.customerNumber,
                                    phoneNumber: formFieldValues.phoneNumber.value,
                                    referenceProductId: formFieldValues.referenceProductId.value,
                                    topic: widgetFields.topic,
                                    onSuccess: () => {
                                        setIsSubmitted(true);
                                    },
                                    onError: () => {
                                        toasterContext.addToast({
                                            body: translate("An error occurred. Try again later."),
                                            messageType: "danger",
                                        });
                                    },
                                    onComplete: () => {
                                        setIsLoading(false);
                                    },
                                }),
                            );
                        }}
                    >
                        <ContactUsTopicsFormContactInformationStep />
                        <ContactUsTopicsFormDetailsStep isPlantRequired={widgetFields.isLocationRequired} />
                    </FormStepper>
                    <NavigationBlocker isActive={isDirty} />
                </>
            )}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: ContactUsTopicsForm,
    definition: {
        displayName: "Contact Us Topics Form",
        group: constants.widgetGroup.contactUsTopics,
        fieldDefinitions: [
            {
                name: fields.topic,
                displayName: "Topic",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ContactUsTopics",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.isLocationRequired,
                displayName: "Is Location Required?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
